import { useMemo, useState } from 'react'

import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'

import type { Country, CountryNode } from '@twisto/api-graphql'
import {
  Modal,
  ModalCloser,
  ModalContent,
} from '@twisto/components/atoms/modal'
import { Typography } from '@twisto/components/atoms/typography'
import { SearchBar } from '@twisto/components/molecules/search-bar'
import { localeIncludes } from '@twisto/utils'

import { CountryOfBirthListItem } from './country-of-birth-list-item'
import * as styles from './country-of-birth-list-modal.styles'

const fieldName = 'nationality'

type Props = {
  countries: Array<
    Pick<CountryNode, 'code' | 'nameTranslated' | 'nameEnglish' | 'flagImage'>
  >
  onSubmit: (code: Country) => void
  opened: boolean
  onClose: () => void
}

// TODO: use one component for customer and flow once Flow schema is merged to customer schema
export const CountryOfBirthListModal = ({
  countries,
  onSubmit,
  onClose,
  opened,
}: Props) => {
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    if (query === '') {
      return countries
    }

    return countries.filter(
      (c) =>
        localeIncludes(c.nameTranslated, query, { normalize: true }) ||
        localeIncludes(c.nameEnglish, query, { normalize: true })
    )
  }, [query, countries])

  return (
    <Modal
      noPadding
      aria-label={/*i18n*/ i18n._('flow.countryOfBirth.modal.title')}
      modalBoxStyles={styles.root}
      opened={opened}
      variant="centered"
      verticalAlign="top"
      onClickOutside={onClose}
    >
      <ModalCloser onClick={onClose} />
      <Typography align="center" fontWeight="medium" variant="h3">
        <Trans id="flow.countryOfBirth.modal.title" />
      </Typography>
      <ModalContent css={styles.content} data-testid={`${fieldName}-form`}>
        <>
          <div css={styles.searchContainer}>
            <SearchBar
              name="document-name"
              placeholder={
                /*i18n*/
                i18n._('flow.countryOfBirth.modal.searchFilter.placeholder')
              }
              searchQuery={query}
              setSearchQuery={(newQuery) => setQuery(newQuery)}
            />
          </div>
          <div key={query}>
            {filtered.map((country) => (
              <CountryOfBirthListItem
                key={country.code}
                country={country}
                showFlag={false}
                onSubmit={onSubmit}
              />
            ))}
          </div>
        </>
      </ModalContent>
    </Modal>
  )
}
